import _ from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import EmergencyPatientSelection, { EmergencyPatientSelectionProps, patientsPerPageOptions } from '../components/EmergencyPatientSelection'

import { CreatePatientDto } from '@/core/models/patient.model'
import { useIsSA } from '@/hooks/useIsSA'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { clearNewlyCreatedPatientId, createPatient, getPatients, selectNewlyCreatedPatientId } from '@/redux/reducers/patient.reducer'
import { useSearchPatientsQuery } from '@/core/services/patient'
import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

export function EmergencyPatientSelectionContainer(props: Pick<EmergencyPatientSelectionProps, 'onSelect'>) {
  const { onSelect } = props
  const authUser = useAppSelector(selectAuthUser)
  const dispatch = useAppDispatch()
  const isSA = useIsSA()

  const newlyCreatedPatientId = useAppSelector(selectNewlyCreatedPatientId)

  const [searchPatientsArgs, setSearchPatientsArgs] = useState({
    patientSource: PatientSourceEnum.MedconnectPatient,
    limit: patientsPerPageOptions[0],
    skip: 0,
    orderBy: 'userInfo.surname',
  })

  const {
    isFetching: isFetchingPatients,
    patientsData,
    totalPatients,
  } = useSearchPatientsQuery(searchPatientsArgs, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      patientsData:
        data?.items.map((patient) => ({
          _id: patient._id,
          name: patient.userInfo.name,
          surname: patient.userInfo.surname,
          gender: patient.userInfo.gender,
          birthday: patient.userInfo.birthday,
          city: patient.contactInfo?.city,
          secu: patient.secu,
        })) ?? [],
      totalPatients: data?.totalCount ?? 0,
    }),
  })

  const handleChangePage = useCallback(
    (newPage: number, rowsPerPage: number) => {
      setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, skip: (newPage - 1) * rowsPerPage }))
    },
    [dispatch]
  )

  const handleCreatePatient = () => {
    const createPatientDto: CreatePatientDto = {
      agreement: 'Emergency',
      userInfo: {
        birthday: new Date().toISOString(),
        gender: 'I',
        name: new Date().toLocaleString().replace(/[/+:,\s]+/g, ''),
        surname: isSA ? 'Emergency' : 'Urgence',
      },
      active: false,
      // added logic to create emergency patient when italian language is selected with secu length 16
      secu: authUser?.userInfo?.language === 'it' ? Math.random().toString().substring(2, 18) : Math.random().toString().substring(2, 17), // secu is required
    }
    dispatch(createPatient(createPatientDto))
  }

  const handleSearchPatient = useDebouncedCallback((textSearch: string) => {
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, patientSource: PatientSourceEnum.MedconnectPatient, skip: 0, smartSearch: textSearch }))
  }, 300)

  useEffect(() => {
    if (newlyCreatedPatientId) onSelect?.(newlyCreatedPatientId)
    return () => {
      dispatch(clearNewlyCreatedPatientId())
    }
  }, [dispatch, newlyCreatedPatientId, onSelect])

  return (
    <EmergencyPatientSelection
      {...props}
      isSearching={isFetchingPatients}
      onCreate={handleCreatePatient}
      onPageChange={handleChangePage}
      onSearch={handleSearchPatient}
      patients={patientsData}
      totalSearchCount={totalPatients || 0}
    />
  )
}

export default EmergencyPatientSelectionContainer
